import { session } from "@hotwired/turbo";
// Turbo is imported for FRAMES ONLY (a lazy <turbo-frame> fills the event page's deferred coverage
// block without a reload). The import side-effect registers the <turbo-frame> element; we then turn
// Turbo DRIVE OFF so full-page navigation is unchanged — Drive would otherwise intercept every link
// and form on the site, an unwanted global behaviour change.
session.drive = false;

// Afterglow front-end entrypoint — bundled by esbuild into app/assets/builds.
//
// The product surfaces are entirely server-rendered (ERB + Tailwind); there is no client-side
// framework. The old React scaffold (react/mount.js + a Hello.jsx that nothing mounted) shipped
// ~1.1 MB of dead JS on every page and was removed (UI review finding #19). This file is
// intentionally near-empty so the build still emits application.js for the layout's include tag —
// add real behaviour here (or wire Turbo/Stimulus, both already in the Gemfile) when a page needs it.

// Copy-permalink enhancement (product batch #3): links marked data-copy-link copy their absolute
// URL to the clipboard instead of navigating, with brief inline feedback. Progressive: without JS
// or outside a secure context (no navigator.clipboard) the click falls through to the default
// anchor navigation, which still lands on the cited row. Delegated once — survives Turbo visits.
// Theme toggle (light ⇄ dark). The <head> init script sets the initial class before paint; this
// only handles the click and persists the choice. Delegated so it survives any future Turbo visit.
document.addEventListener("click", (event) => {
  if (!event.target.closest("[data-theme-toggle]")) return;
  const dark = document.documentElement.classList.toggle("dark");
  try { localStorage.setItem("theme", dark ? "dark" : "light"); } catch (e) {}
});

// "Twoja bańka" (/bubble) picks stay in the URL (a shareable GET form — see BubbleController), but
// a returning visitor shouldn't have to re-check the same portals every time. localStorage only:
// no accounts, nothing server-side. Pre-checks on load ONLY when the URL carries no picks of its
// own (a shared link with ?sources=... always wins over a stored pick), and saves on submit so the
// next visit starts from what was actually shown, not merely hovered.
document.addEventListener("DOMContentLoaded", () => {
  const form = document.getElementById("bubble-form");
  if (!form) return;

  const checkboxes = () => Array.from(form.querySelectorAll('input[name="sources[]"]'));

  if (!new URLSearchParams(window.location.search).has("sources[]")) {
    try {
      const saved = JSON.parse(localStorage.getItem("bubble-sources") || "[]");
      checkboxes().forEach((box) => { box.checked = saved.includes(box.value); });
      // A remembered pick loads the bubble at once (design pass 2026-09: the page used to show the
      // saved chips ticked and, under them, "wybierz co najmniej jeden portal"). Submitting with no
      // pick saves [] first, so an emptied pick never loops back here.
      if (saved.length && form.dataset.autoload === "true") form.requestSubmit();
    } catch (e) {}
  }

  // The landing lens applies on click; here the same chip submits the GET form (URL stays shareable).
  form.addEventListener("change", (event) => {
    if (event.target.matches('input[name="sources[]"]')) form.requestSubmit();
  });

  // <sm the picker folds to "Czytasz: … · zmień" once a pick exists.
  const toggle = form.querySelector("[data-picker-toggle]");
  const chips = form.querySelector("[data-picker-chips]");
  if (toggle && chips) {
    toggle.addEventListener("click", () => {
      const open = chips.classList.toggle("hidden");
      toggle.setAttribute("aria-expanded", open ? "false" : "true");
    });
  }

  form.addEventListener("submit", () => {
    try {
      const picked = checkboxes().filter((box) => box.checked).map((box) => box.value);
      localStorage.setItem("bubble-sources", JSON.stringify(picked));
    } catch (e) {}
  });
});

// "Twoja bańka" outside-list prominence filter (Wszystkie / Były na stronie głównej / Czołówka lub
// wysoko / Tylko czołówka). Delegated (not DOMContentLoaded-scoped) because #bubble-outside can
// arrive later via the results turbo-frame swap when the combination was still @pending on first
// load — a scoped listener attached at DOMContentLoaded would miss buttons that don't exist yet.
// Filtering is pure show/hide over the already server-rendered <li> list — no re-fetch, no
// re-render, each <li>'s data-bubble-filter-buckets was computed once server-side.
document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-bubble-filter]");
  if (!button) return;

  const container = button.closest("#bubble-outside");
  if (!container) return;

  const filter = button.dataset.bubbleFilter;
  container.querySelectorAll("[data-bubble-filter]").forEach((btn) => {
    btn.setAttribute("aria-pressed", btn === button ? "true" : "false");
  });

  let visibleCount = 0;
  container.querySelectorAll("li[data-bubble-filter-buckets]").forEach((li) => {
    const buckets = li.dataset.bubbleFilterBuckets.split(" ");
    const show = buckets.includes(filter);
    li.hidden = !show;
    if (show) visibleCount += 1;
  });

  const empty = container.querySelector("[data-bubble-filter-empty]");
  if (empty) empty.hidden = visibleCount > 0;

  // Visible proof the filter did something — the top of the list often already satisfies every
  // filter, so rows alone can look unchanged even when others further down got hidden (live find
  // 2026-08-20: user clicked a filter, saw no visible movement, asked if it worked at all).
  const count = container.querySelector("[data-bubble-filter-count]");
  if (count) count.textContent = visibleCount;
});

document.addEventListener("click", (event) => {
  const link = event.target.closest("a[data-copy-link]");
  if (!link || !navigator.clipboard) return;

  event.preventDefault();
  navigator.clipboard.writeText(link.href).then(() => {
    if (link.dataset.copied) return; // already showing feedback
    link.dataset.copied = "1";
    const label = link.textContent;
    link.textContent = "skopiowano ✓";
    setTimeout(() => {
      link.textContent = label;
      delete link.dataset.copied;
    }, 1500);
  });
});


// Soczewka (design pass 2026-09): the reader's own outlets as a lens over the coverage grid. Shares
// localStorage["bubble-sources"] (slugs) with /bubble so the two surfaces agree. Purely client-side
// over the server-rendered grid: columns of unpicked outlets dim, rows none of the picked outlets
// covered get their "poza Twoją bańką" badge, and the barometer counts what the pick lets through.
// An empty/absent pick means "all on" (the full census) — nothing is ever hidden, only dimmed.
const LENS_KEY = "bubble-sources";
function lensInit(root) {
  const lens = root.querySelector("[data-lens]");
  if (!lens || lens.dataset.lensReady) return;
  lens.dataset.lensReady = "1";
  const chips = Array.from(lens.querySelectorAll("[data-lens-chip]"));
  const all = chips.map((c) => c.dataset.lensChip);
  let picked = [];
  try { picked = JSON.parse(localStorage.getItem(LENS_KEY) || "[]").filter((s) => all.includes(s)); } catch (e) {}
  const active = () => (picked.length ? picked : all);

  const apply = () => {
    const on = new Set(active());
    chips.forEach((c) => c.setAttribute("aria-pressed", on.has(c.dataset.lensChip) ? "true" : "false"));
    document.querySelectorAll("[data-outlet]").forEach((el) => el.classList.toggle("lens-off", !on.has(el.dataset.outlet)));
    document.querySelectorAll("[data-lens-row]").forEach((row) => {
      const inside = Array.from(row.querySelectorAll("[data-outlet][data-covered='1']")).some((el) => on.has(el.dataset.outlet));
      row.classList.toggle("lens-outside", !inside);
    });
    // Re-order every list (desktop tbody, mobile cards): rows OUTSIDE the pick first, a divider, then
    // the rows inside it — narrowing the lens moves the page, not just a badge. Each row drags its
    // chorus panel along; everything else in the list (recency break, narrow block, the fold) follows.
    // All on → the original order (remembered on first apply), divider removed.
    const narrowed = on.size < all.length;
    document.querySelectorAll("[data-lens-list]").forEach((list) => {
      list.querySelector("[data-lens-divider]")?.remove();
      const children = Array.from(list.children);
      children.forEach((el, k) => { if (el.dataset.origIdx === undefined) el.dataset.origIdx = String(k); });
      if (!narrowed) {
        children.slice().sort((x, y) => +x.dataset.origIdx - +y.dataset.origIdx).forEach((el) => list.appendChild(el));
        return;
      }
      const rows = children.filter((el) => el.hasAttribute("data-lens-row") && el.dataset.lensRow !== "aux");
      const panelOf = (r) => list.querySelector(`:scope > [data-chorus-of='${r.dataset.i}']`);
      const others = children.filter((el) => !rows.includes(el) && !el.hasAttribute("data-chorus-of"));
      const outside = rows.filter((r) => r.classList.contains("lens-outside"));
      const inside = rows.filter((r) => !r.classList.contains("lens-outside"));
      const seq = [];
      outside.forEach((r) => { seq.push(r); const p = panelOf(r); if (p) seq.push(p); });
      // A divider only when there is something above it to separate (a colspan row as the FIRST row
      // of a table-fixed table collapses its column widths — seen live).
      if (outside.length && inside.length) {
        const div = document.createElement(list.tagName === "TBODY" ? "tr" : "div");
        div.setAttribute("data-lens-divider", "");
        div.innerHTML = list.tagName === "TBODY"
          ? `<td colspan="${list.dataset.cols || 2}" class="border-t border-line bg-canvas px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-accent">poniżej: w Twojej bańce · ${inside.length}</td>`
          : `<div class="pt-1 text-[11px] font-medium uppercase tracking-wide text-accent">poniżej: w Twojej bańce · ${inside.length}</div>`;
        seq.push(div);
      }
      inside.forEach((r) => { seq.push(r); const p = panelOf(r); if (p) seq.push(p); });
      others.forEach((el) => seq.push(el));
      seq.forEach((el) => list.appendChild(el));
    });
    // Event page: no rows to count — the question is whether THIS event reached the reader's pick.
    if (lens.dataset.lensMode === "event") {
      const covered = new Set(Array.from(document.querySelectorAll("[data-outlet][data-covered='1']")).map((el) => el.dataset.outlet));
      const mine = chips.filter((c) => on.has(c.dataset.lensChip));
      const hits = mine.filter((c) => covered.has(c.dataset.lensChip)).map((c) => c.textContent.trim());
      const t = lens.querySelector("[data-lens-text]");
      if (on.size === all.length) t.textContent = "Wybierz portale, które czytasz, a powiemy, czy ten temat trafiłby do Twojej bańki.";
      else if (!on.size) t.textContent = "Żadnej redakcji nie włączono.";
      else if (hits.length) t.textContent = `W Twojej bańce: ten temat pokazało ${hits.length} z ${on.size} Twoich redakcji (${hits.join(", ")}). Wygaszone głosy to nagłówki, których nie zobaczyłeś.`;
      else t.textContent = `Poza Twoją bańką: u żadnej z Twoich ${on.size} redakcji nie zaobserwowaliśmy tego tematu (co to dokładnie znaczy — patrz „Nie zaobserwowano u" niżej).`;
      const allBtn2 = lens.querySelector("[data-lens-all]");
      if (allBtn2) allBtn2.textContent = on.size === all.length ? "wyłącz wszystkie" : "wszystkie";
      return;
    }
    // The barometer counts grid EVENTS once: the desktop <tr>s (the mobile cards duplicate them,
    // and the "Wysoko, ale wąsko" rows are marked aux — badged, not counted).
    const gridRows = Array.from(document.querySelectorAll("tr[data-lens-row]"));
    const rows = gridRows.length;
    const seenRows = gridRows.filter((r) => !r.classList.contains("lens-outside")).length;
    lens.querySelector("[data-lens-total]").textContent = rows;
    lens.querySelector("[data-lens-seen]").textContent = seenRows;
    const text = lens.querySelector("[data-lens-text]");
    const allOn = on.size === all.length;
    // One line, whichever side is shorter to name: the few switched OFF, the few switched ON, or
    // just the count when both lists would run long.
    const onNames  = chips.filter((c) => on.has(c.dataset.lensChip)).map((c) => c.textContent.trim());
    const offNames = chips.filter((c) => !on.has(c.dataset.lensChip)).map((c) => c.textContent.trim());
    const pick = on.size === 0 ? "żadnej redakcji"
      : offNames.length <= 3 ? `wszystkie redakcje poza: ${offNames.join(", ")}`
      : onNames.length <= 3 ? onNames.join(", ")
      : `${onNames.length} z ${all.length} redakcji`;
    text.textContent = allOn
      ? "wydarzeń dnia · wszystkie redakcje włączone"
      : `wydarzeń dnia w Twojej bańce (${pick}) · ${rows - seenRows} poza nią, oznaczone w siatce.`;
    const allBtn = lens.querySelector("[data-lens-all]");
    if (allBtn) allBtn.textContent = allOn ? "wyłącz wszystkie" : "wszystkie";
    const link = lens.querySelector("[data-lens-link]");
    if (link) {
      const url = new URL(lens.dataset.bubblePath, window.location.origin);
      url.searchParams.delete("sources[]");
      if (!allOn) active().forEach((s) => url.searchParams.append("sources[]", s));
      link.href = url.pathname + url.search;
    }
  };
  const save = () => { try { localStorage.setItem(LENS_KEY, JSON.stringify(picked)); } catch (e) {} };

  chips.forEach((c) => c.addEventListener("click", () => {
    const slug = c.dataset.lensChip;
    const on = new Set(active());
    on.has(slug) ? on.delete(slug) : on.add(slug);
    picked = on.size === all.length ? [] : all.filter((s) => on.has(s));
    save(); apply();
  }));
  const allBtn = lens.querySelector("[data-lens-all]");
  if (allBtn) allBtn.addEventListener("click", () => {
    picked = (active().length === all.length) ? ["__none__"] : [];
    save(); apply();
  });
  apply();
}
document.addEventListener("DOMContentLoaded", () => lensInit(document));
// The past-day grid arrives later inside <turbo-frame id="events-grid">.
document.addEventListener("turbo:frame-load", (e) => lensInit(e.target));


// Chór (design pass 2026-09): a grid row's "chór ▾" reveals the row/panel below it; the lazy
// <turbo-frame> inside fetches the outlets' headlines only once it becomes visible. Delegated, so it
// works for rows that arrive later in the deferred past-day frame.
document.addEventListener("click", (event) => {
  const btn = event.target.closest("[data-chorus-toggle]");
  if (!btn) return;
  const panel = document.getElementById(btn.dataset.chorusToggle);
  if (!panel) return;
  const open = panel.hidden;
  panel.hidden = !open;
  panel.classList.toggle("hidden", !open);
  btn.setAttribute("aria-expanded", open ? "true" : "false");
  btn.textContent = open ? "nagłówki ▴" : "nagłówki ▾";
});


// Three-question nav: one open at a time, closes on outside click / Escape. <details> already works
// without this; the script only keeps two dropdowns from stacking.
document.addEventListener("toggle", (event) => {
  const d = event.target;
  if (!(d instanceof HTMLDetailsElement) || !d.hasAttribute("data-nav-q") || !d.open) return;
  document.querySelectorAll("details[data-nav-q][open]").forEach((o) => { if (o !== d) o.open = false; });
}, true);
document.addEventListener("click", (event) => {
  if (event.target.closest("details[data-nav-q]")) return;
  document.querySelectorAll("details[data-nav-q][open]").forEach((o) => { o.open = false; });
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape") document.querySelectorAll("details[data-nav-q][open]").forEach((o) => { o.open = false; });
});


// Grid fold: rows past the first screen carry .fold-row; the button toggles .fold-open on the wrapper.
document.addEventListener("click", (event) => {
  const btn = event.target.closest("[data-fold-toggle]");
  if (!btn) return;
  const wrap = btn.closest("[data-grid-fold]");
  if (!wrap) return;
  const open = wrap.classList.toggle("fold-open");
  btn.setAttribute("aria-expanded", open ? "true" : "false");
  btn.textContent = open ? btn.dataset.labelOpen : btn.dataset.labelClosed;
});

// /articles/:id/edits folds additions-only edits into a closed <details>. A cited change
// (#e-<snapshot>) may sit inside one: open every enclosing group so the anchor lands on the row.
// Chromium/Firefox already do this for fragment navigation; this covers the rest and hashchange.
const revealHashTarget = () => {
  const id = decodeURIComponent(location.hash.slice(1));
  if (!id) return;
  const target = document.getElementById(id);
  if (!target) return;
  let group = target.closest("details");
  let opened = false;
  while (group) {
    if (!group.open) { group.open = true; opened = true; }
    group = group.parentElement && group.parentElement.closest("details");
  }
  if (opened) target.scrollIntoView({ block: "start" });
};
document.addEventListener("DOMContentLoaded", revealHashTarget);
window.addEventListener("hashchange", revealHashTarget);

// Day toolbars (/silent-edits): the date text sits over a transparent <input type=date>; a click
// opens the native picker (showPicker needs a user gesture), a change submits the GET form at once.
// Without JS the <noscript> "Pokaż" button submits the same form.
document.addEventListener("click", (event) => {
  const input = event.target.closest("[data-date-input]");
  if (!input || typeof input.showPicker !== "function") return;
  try { input.showPicker(); } catch (e) {}
});
document.addEventListener("change", (event) => {
  const input = event.target.closest("[data-date-input]");
  if (!input) return;
  const form = input.closest("form");
  if (!form) return;
  if (typeof form.requestSubmit === "function") form.requestSubmit(); else form.submit();
});

// /compare: the pair picker submits on change (two native selects; the <noscript> button is the fallback).
document.addEventListener("change", (event) => {
  const form = event.target.closest("form[data-autosubmit]");
  if (!form || !event.target.matches("select")) return;
  if (typeof form.requestSubmit === "function") form.requestSubmit(); else form.submit();
});

// /flows heatmap: on a phone the scroll container opens on the OLDEST end; start at the newest.
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-scroll-end]").forEach((el) => { el.scrollLeft = el.scrollWidth; });
});
